import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errorDetails, errorMessage } from '../lib/api';
import { formatDuration, type Diagnostic, type ImportOutcome, type TestModule, type TestSummary } from '../lib/tests';
import { ModuleTag } from '../components/ModuleTag';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { useConfirm } from '../components/Confirm';
import { DiagnosticList } from '../components/DiagnosticList';
import { SortHeader } from '../components/SortHeader';
import { Sheet } from '../components/Sheet';
import { AssignTest } from '../components/AssignTest';
import { nextSort, type SortState } from '../lib/sorting';
import {
  arrangePapers,
  countShelf,
  onShelf,
  searchPapers,
  type PaperSortKey,
  type Shelf,
} from '../lib/paper-list';
import {
  COLLECTIONS,
  COLLECTION_LABEL,
  MODULE_LABEL,
  booksOf,
  groupIntoTests,
  namesAnActualTest,
  splitByCollection,
  whatIsMissing,
  type Book,
  type Collection,
  type Module,
  type WholeTest,
} from '../lib/library';

/**
 * Which books the teacher left open, kept for the tab they are working in.
 *
 * sessionStorage rather than memory because the library is a place you leave
 * and come back to — open Cambridge 19, read a Reading paper, press back —
 * and a book that closes itself every time is worse than one that never
 * opened. Not localStorage: a shared staff machine should not hand the next
 * teacher the last one's shelf.
 *
 * Every read and write is guarded. Storage throws in a private window and
 * comes back empty after it is cleared, and neither is a reason for the test
 * library to fail to draw.
 */
const BOOKS_KEY = 'target.books.v1';

function rememberedBooks(): Record<string, boolean> {
  try {
    const raw = sessionStorage.getItem(BOOKS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, open]) => typeof open === 'boolean'),
    ) as Record<string, boolean>;
  } catch {
    return {};
  }
}

function rememberBooks(open: Record<string, boolean>): void {
  try {
    sessionStorage.setItem(BOOKS_KEY, JSON.stringify(open));
  } catch {
    /* Nothing to do: the shelf still works, it just forgets. */
  }
}

/** An id for a heading's panel, from a name that may hold anything. */
const bookId = (label: string) => `book-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

/**
 * The library's columns, defined once and used by every table in it.
 *
 * They were written out three times — once per table — and drifted: the
 * module column was too narrow for "Listening (spare)" and the questions
 * column too narrow for its own heading once a sort arrow sat beside it, so
 * both showed an ellipsis where a word should be. Three copies of a
 * measurement is three chances to be wrong about it.
 *
 * Widths are for the longest thing each column actually holds, headings
 * included: a heading that does not fit is the one piece of text on the
 * screen that cannot be worked out from context.
 */
function LibraryColumns({ compact = false }: { compact?: boolean }) {
  return (
    <colgroup>
      {/* "Listening (spare)" is the longest label. */}
      <col style={{ width: '9.5rem' }} />
      <col />
      <col style={{ width: compact ? '6.75rem' : '7.5rem' }} />
      {/* "QUESTIONS", plus its sort arrow where there is one. */}
      <col style={{ width: compact ? '7.5rem' : '8rem' }} />
      <col style={{ width: '8.5rem' }} />
      {/*
        * When a paper arrived is a question the loose papers are sorted by.
        * Inside a test card it is one more column between the paper and its
        * buttons on a row that has to fit a laptop — and the date is on the
        * paper's own page, one click away.
        */}
      {!compact && <col style={{ width: '7.5rem' }} />}
      {/*
        * However wide the buttons of this particular table need. Set on the
        * table itself, because the same number also decides where the table
        * starts scrolling — and when the two were written separately the
        * wider one squeezed the paper's own name to six characters.
        */}
      <col style={{ width: 'var(--actions)' }} />
    </colgroup>
  );
}

const STATUS_TONE: Record<string, string> = {
  PUBLISHED: 'tag--on',
  DRAFT: '',
  ARCHIVED: 'tag--off',
};

export function TestsPage() {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [tests, setTests] = useState<TestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<Diagnostic[] | null>(null);

  // Offered only where it is configured: a deployment with no API key should
  // not show a button that cannot work.
  const [photoImport, setPhotoImport] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoForm, setPhotoForm] = useState({ module: 'READING', title: '' });
  const [pages, setPages] = useState<File[]>([]);
  const [working, setWorking] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const [query, setQuery] = useState('');
  /**
   * Which shelf the library is showing.
   *
   * A centre accumulates papers for years, and the ones it is teaching from
   * this term are a handful of them. Both shelves can be set on a class — a
   * past paper is old, not withdrawn — so this is where a paper is kept, not
   * what may be done with it.
   */
  const [shelf, setShelf] = useState<Shelf>('CURRENT');
  const [sort, setSort] = useState<SortState<PaperSortKey>>({
    key: 'imported',
    direction: 'desc',
  });

  function sortBy(key: PaperSortKey) {
    // Each column starts at the end that answers the question it is clicked
    // for: most questions and newest first, but a title A to Z — and status
    // ascending, because the status order is drafts, then in use, then
    // archived, and "what have I not finished?" is why anybody sorts by it.
    const fresh = key === 'questions' || key === 'imported' ? 'desc' : 'asc';
    setSort((current) => nextSort(current, key, fresh));
  }

  const shelved = useMemo(() => onShelf(tests, shelf), [tests, shelf]);
  const current = useMemo(() => countShelf(tests, 'CURRENT'), [tests]);
  const kept = useMemo(() => countShelf(tests, 'PAST'), [tests]);

  /** Every paper the search leaves, before it is split back into its module. */
  const found = useMemo(() => searchPapers(shelved, query), [shelved, query]);

  /** Moving a paper between the shelves. */
  async function moveTo(test: TestSummary, next: Shelf) {
    setWorking(test.id);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/tests/${test.id}`, { shelf: next });
      setNotice(
        next === 'PAST'
          ? `“${test.title}” moved to past papers. It can still be set on a class.`
          : `“${test.title}” is back among the current papers.`,
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'That paper could not be moved'));
    } finally {
      setWorking(null);
    }
  }
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * The whole test whose register is open, where one is.
   *
   * The test itself rather than its name: a centre can hold its own "Test 1"
   * and a lent "Test 1", and the register has to be opened over the one that
   * was actually clicked.
   */
  const [setting, setSetting] = useState<WholeTest | null>(null);

  /**
   * The library, as whole tests and what is left over.
   *
   * Built from the shelf on screen rather than the whole library: a test
   * assembled across both shelves would show a paper as present that the
   * teacher cannot see anywhere beneath it, and "not added yet" for one that
   * is merely put away.
   */
  const library = useMemo(() => groupIntoTests(shelved), [shelved]);
  /** The shelf in its two named halves, each in its own order. */
  const split = useMemo(() => splitByCollection(library.tests), [library.tests]);
  /**
   * Which half is on screen.
   *
   * Null until a teacher chooses one, and until then whichever half has
   * something in it — preferring the actual tests, which is what a centre
   * reaches for. Showing both at once is what this replaced: three whole tests
   * stacked, each with its three papers listed under it, is already more
   * scrolling than a library ought to ask for, and a shelf that grows all year
   * only gets worse.
   */
  const [halfChosen, setHalfChosen] = useState<Collection | null>(null);
  /** The test whose name is being edited, and what it is being changed to. */
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  /**
   * Which books the teacher has opened or closed by hand.
   *
   * Only the ones they have touched: everything else follows the default
   * below, so a book imported next month arrives closed like its neighbours
   * rather than inheriting a decision made about a different shelf.
   */
  const [openBooks, setOpenBooks] = useState<Record<string, boolean>>(rememberedBooks);
  const half: Collection = halfChosen ?? (split.ACTUAL.length > 0 ? 'ACTUAL' : 'GENERAL');

  /**
   * Which papers the search leaves standing.
   *
   * The tests are built from the whole shelf rather than from the search, so
   * a card never says "no Writing yet" about a test that has one and simply
   * did not match what was typed. The search decides which rows are shown
   * inside the cards, and which cards are worth showing at all.
   */
  const searching = query.trim().length > 0;
  const matches = useMemo(() => new Set(found.map((paper) => paper.id)), [found]);
  const shows = (paper: TestSummary) => !searching || matches.has(paper.id);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<{ tests: TestSummary[] }>('/tests');
      setTests(data.tests);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load your test library'));
    } finally {
      setLoading(false);
    }
  }

  /** Where what-just-happened is said, so it can be scrolled to. */
  const saying = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!error && !notice) return;
    // Not smooth: a smooth scroll is a request the browser is free to ignore,
    // and one that quietly did nothing here would leave the message exactly as
    // unread as having no scroll at all. The point is that it is seen.
    saying.current?.scrollIntoView({ block: 'nearest' });
  }, [error, notice]);

  useEffect(() => {
    void load();
    api
      .get<{ photoImport: boolean }>('/tests/capabilities')
      .then(({ data }) => setPhotoImport(data.photoImport))
      .catch(() => setPhotoImport(false));
  }, []);

  async function importFile(file: File) {
    setImporting(true);
    setError(null);
    setRejected(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post<ImportOutcome>('/tests/import', form);
      // Straight to review: an import is not finished until someone has looked
      // at what the parser made of it.
      navigate(`/tests/${data.testId}`);
    } catch (err) {
      setError(errorMessage(err, 'That file could not be imported'));
      const details = errorDetails<{ diagnostics?: Diagnostic[] }>(err);
      setRejected(details?.diagnostics ?? null);
    } finally {
      setImporting(false);
    }
  }

  /**
   * Taking a paper out of circulation. Always available, and always safe: the
   * paper and every result against it stay exactly where they are.
   */
  async function archive(test: TestSummary) {
    const yes = await ask({
      title: `Archive “${test.title}”?`,
      body: <p>Students can no longer be assigned it. Nothing is lost, and it can be unarchived.</p>,
      confirmLabel: 'Archive paper',
    });
    if (!yes) return;
    setWorking(test.id);
    setError(null);
    setNotice(null);
    try {
      await api.post(`/tests/${test.id}/archive`);
      setNotice(`"${test.title}" was archived.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'That paper could not be archived'));
    } finally {
      setWorking(null);
    }
  }

  /**
   * Deleting one outright. The server refuses if anybody has sat it, because
   * everything hangs off the test by a cascade and the results would go too —
   * its refusal says so, and is shown rather than swallowed.
   */
  async function remove(test: TestSummary) {
    const yes = await ask({
      title: `Delete “${test.title}” permanently?`,
      body: (
        <>
          {/* Which test loses a paper by this. A centre that deleted what it
              took for spare copies found it had deleted the papers its tests
              were made of — they were the same papers, shown twice. */}
          {test.setName && (
            <p>
              This is the {test.module.toLowerCase()} paper of{' '}
              <strong>{test.setName}</strong>. That test will be left without one.
            </p>
          )}
          <p>This cannot be undone.</p>
          <p>If anybody has already sat it the server will refuse — archive it instead.</p>
        </>
      ),
      confirmLabel: 'Delete paper',
      tone: 'danger',
    });
    if (!yes) return;
    setWorking(test.id);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/tests/${test.id}`);
      setNotice(`"${test.title}" was deleted.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'That paper could not be deleted'));
    } finally {
      setWorking(null);
    }
  }

  /**
   * A whole test across to the other shelf: all three papers at once.
   *
   * A test is one thing to a centre, and moving it a paper at a time is three
   * chances to move two of them and forget the third — which then shows as a
   * test with a Writing paper missing on one shelf and a Writing paper alone
   * on the other.
   */
  async function moveTest(test: WholeTest, next: Shelf) {
    setWorking(test.name);
    setError(null);
    setNotice(null);
    let failed: string | null = null;
    try {
      for (const paper of test.papers) {
        await api.patch(`/tests/${paper.id}`, { shelf: next });
      }
    } catch (err) {
      failed = errorMessage(err, 'That test could not be moved');
    }
    // Reloading clears whatever was on screen, so what happened is said after
    // the reload rather than before it — which is how a message meant for the
    // teacher used to be wiped a tenth of a second after it appeared.
    await load();
    if (failed) setError(failed);
    else {
      setNotice(
        next === 'PAST'
          ? `${test.name} moved to past papers. It can still be set on a class.`
          : `${test.name} is back among the current tests.`,
      );
    }
    setWorking(null);
  }

  /**
   * Renaming a whole test.
   *
   * The name is the grouping: three papers carrying it are one test. So this
   * has to reach all three, and renaming them one at a time on three separate
   * review screens is how a test ends up split in two — "Test 4" with a
   * Listening in it and "Test 4 revised" with the other two. It was the only
   * way to do it, which is to say there was no way to do it.
   */
  async function renameTest(test: WholeTest, raw: string) {
    const next = raw.trim();
    if (!next || next === test.name) {
      setRenaming(null);
      return;
    }

    // A name another test already carries merges the two, because the name is
    // all the grouping there is. That is occasionally what somebody means and
    // never what they expect, so it is said out loud first.
    const clash = library.tests.find((t) => t !== test && t.name === next && t.owned === test.owned);
    if (clash) {
      const merge = await ask({
        title: `${next} already exists`,
        body: `A test is its name, so "${test.name}" would join it — ${
          clash.papers.length + test.papers.length
        } papers under one name, and the spares listed as spares.`,
        confirmLabel: 'Merge them',
      });
      if (!merge) return;
    }

    setWorking(test.name);
    setError(null);
    setNotice(null);
    let failed: string | null = null;
    try {
      for (const paper of test.papers) {
        await api.patch(`/tests/${paper.id}`, { setName: next });
      }
    } catch (err) {
      failed = errorMessage(err, 'That test could not be renamed');
    }
    setRenaming(null);
    await load();
    if (failed) setError(failed);
    else {
      // A name that says "actual" files the test under Actual Tests, so the
      // card leaves the shelf being looked at. Follow it, rather than leave a
      // teacher staring at the place it used to be.
      // Against where the test actually is, not against its old name: one
      // filed by hand already sits under Actual Tests, and telling a teacher
      // it has just been moved there is telling them something that did not
      // happen.
      const moved = namesAnActualTest(next) && test.collection !== 'ACTUAL';
      if (moved) setHalfChosen('ACTUAL');
      setNotice(
        moved
          ? `Renamed to ${next}, and filed under Actual Tests.`
          : `${test.name} is now ${next}.`,
      );
    }
    setWorking(null);
  }

  /**
   * A whole test from one half of the library to the other.
   *
   * Filed a paper at a time underneath, like the shelf move above and for the
   * same reason: a test is one thing to a centre, and the three papers must
   * end up together or the test appears under both headings at once.
   */
  async function fileTest(test: WholeTest, next: Collection) {
    setWorking(test.name);
    setError(null);
    setNotice(null);
    let failed: string | null = null;
    try {
      for (const paper of test.papers) {
        await api.patch(`/tests/${paper.id}`, { collection: next });
      }
    } catch (err) {
      failed = errorMessage(err, 'That test could not be filed');
    }
    await load();
    if (failed) setError(failed);
    else {
      setNotice(
        next === 'ACTUAL'
          ? `${test.name} is now under Actual Tests.`
          : `${test.name} is now under Tests.`,
      );
    }
    setWorking(null);
  }

  /**
   * Deleting a whole test.
   *
   * This exists because it is what a centre means when it clears one out, and
   * because doing it a paper at a time is how three papers get deleted by
   * somebody who meant to delete none of them. The server still refuses each
   * paper on its own terms — a published one, or one somebody has sat — so a
   * test can come back half deleted, and what was kept is named rather than
   * left to be discovered.
   */
  async function removeTest(test: WholeTest) {
    const yes = await ask({
      title: `Delete ${test.name} permanently?`,
      body: (
        <>
          <p>
            All {test.papers.length} of its papers go: {test.papers.map((p) => p.title).join(', ')}.
          </p>
          <p>This cannot be undone.</p>
          <p>
            A paper that is published, or that anybody has already sat, will be kept — archive it
            instead.
          </p>
        </>
      ),
      confirmLabel: `Delete ${test.papers.length} papers`,
      tone: 'danger',
    });
    if (!yes) return;

    setWorking(test.name);
    setError(null);
    setNotice(null);
    const kept: string[] = [];
    let gone = 0;
    for (const paper of test.papers) {
      try {
        await api.delete(`/tests/${paper.id}`);
        gone += 1;
      } catch (err) {
        kept.push(`“${paper.title}” — ${errorMessage(err, 'could not be deleted')}`);
      }
    }
    // After the reload, for the same reason as above.
    await load();
    if (gone > 0) {
      setNotice(`${gone} ${gone === 1 ? 'paper' : 'papers'} of ${test.name} deleted.`);
    }
    if (kept.length > 0) {
      setError(
        `${kept.length === test.papers.length ? 'Nothing was deleted. ' : ''}Kept: ${kept.join(' ')}`,
      );
    }
    setWorking(null);
  }

  /**
   * Lending a whole test to every centre on the platform, or taking it back.
   *
   * Per test rather than per paper, because a test lent without its Reading
   * is not a test anybody can teach from — and because the centre borrowing
   * it thinks in whole tests too.
   */
  async function shareTest(test: WholeTest, next: boolean) {
    setWorking(test.name);
    setError(null);
    setNotice(null);
    let failed: string | null = null;
    try {
      for (const paper of test.papers) {
        await api.patch(`/tests/${paper.id}`, { shared: next });
      }
    } catch (err) {
      failed = errorMessage(err, 'That test could not be shared');
    }
    await load();
    if (failed) setError(failed);
    else {
      setNotice(
        next
          ? `${test.name} is now in every centre's library. They can set it on their classes; only you can change it.`
          : `${test.name} is yours alone again. Centres that have already set it keep what they set.`,
      );
    }
    setWorking(null);
  }

  async function importPhotos() {
    setImporting(true);
    setError(null);
    setRejected(null);
    try {
      const form = new FormData();
      form.append('module', photoForm.module);
      form.append('title', photoForm.title);
      for (const page of pages) form.append('pages', page);
      const { data } = await api.post<ImportOutcome>('/tests/import/photos', form);
      // Straight to review, as with a file — more so here, because a
      // transcription is a reading of the paper rather than the paper itself.
      navigate(`/tests/${data.testId}`);
    } catch (err) {
      setError(errorMessage(err, 'Those pages could not be imported'));
      const details = errorDetails<{ diagnostics?: Diagnostic[] }>(err);
      setRejected(details?.diagnostics ?? null);
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      {dialog}
      <PageHeader
        title="Test library"
        meta={
          <p className="muted">
            <span className="mono">{tests.length}</span> paper{tests.length === 1 ? '' : 's'}
          </p>
        }
        actions={
          <>
            <a className="btn" href="/api/v1/tests/template.docx" download>
              Download template
            </a>
            {photoImport && (
              <button
                className="btn"
                disabled={importing}
                onClick={() => setPhotoOpen((v) => !v)}
              >
                {photoOpen ? 'Cancel' : 'Import from photos'}
              </button>
            )}
            <button
              className="btn"
              disabled={importing}
              onClick={() => fileInput.current?.click()}
            >
              {importing ? 'Reading the paper…' : 'Import a file'}
            </button>
            {/* The shortest way in, so the one offered first: a paper copied
                out of a book is text, and this takes it as text. */}
            <button className="btn btn--primary" onClick={() => navigate('/tests/new')}>
              Paste a paper
            </button>
          </>
        }
      />

      <input
        ref={fileInput}
        type="file"
        accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void importFile(file);
          e.target.value = '';
        }}
      />

      {/*
        * The register for one whole test, opened over the library. The library
        * stays behind it, because setting a test is something you do while
        * looking at your tests.
        */}
      <Sheet
        open={setting !== null}
        title={setting?.name ?? ''}
        eyebrow="Set on a class"
        onClose={() => setSetting(null)}
      >
        {setting && (
          <AssignTest
            setName={setting.name}
            /*
             * One paper per module — what the card shows — and not the spares
             * beside them. A test set with its spares gives a candidate two
             * Listening papers to sit, which is not a test.
             */
            papers={setting.slots
              .map((slot) => slot.paper)
              .filter((paper): paper is TestSummary => Boolean(paper))
              .map((paper) => ({ id: paper.id, module: paper.module, status: paper.status }))}
          />
        )}
      </Sheet>

      <div className="stack">
        {/*
          * What just happened, and where it can be read.
          *
          * The library is long, and the button that fails is usually near the
          * bottom of it. A message here alone was a message nobody saw: the
          * click looked like it had done nothing at all. So when one appears,
          * it is brought to the eye rather than waiting to be scrolled to.
          */}
        <div ref={saying}>
          {error && <Banner tone="error">{error}</Banner>}
          {notice && <Banner tone="success">{notice}</Banner>}
        </div>

        {photoOpen && (
          <section className="panel stack rise">
            <div>
              <h2 className="panel__title">Import from photographs</h2>
              <p className="muted" style={{ maxWidth: '62ch' }}>
                For a paper that exists only on paper. The pages are read into the same
                template a Word file uses, so everything downstream is unchanged — the
                validator, the answer-key checks, and the review screen you land on next.
              </p>
            </div>

            <Banner tone="warning">
              A transcription is a reading of the paper, not the paper. Check every question
              and every answer on the review screen before you publish — a misread answer key
              marks a correct answer wrong, silently, for everybody who sits it.
            </Banner>

            <div className="form-grid">
              <label className="field">
                <span className="field__label">Module</span>
                <select
                  className="field__input"
                  value={photoForm.module}
                  onChange={(e) => setPhotoForm((f) => ({ ...f, module: e.target.value }))}
                >
                  <option value="LISTENING">Listening</option>
                  <option value="READING">Reading</option>
                  <option value="WRITING">Writing</option>
                </select>
              </label>

              <label className="field">
                <span className="field__label">Title</span>
                <input
                  className="field__input"
                  value={photoForm.title}
                  placeholder="Practice Test 1"
                  onChange={(e) => setPhotoForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
            </div>

            <label className="field">
              <span className="field__label">Pages</span>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => setPages(Array.from(e.target.files ?? []))}
              />
              <span className="field__hint">
                Every page of the paper, in order, including the answer key — as images, or
                as one scanned PDF of the whole paper.
              </span>
            </label>

            {pages.length > 0 && (
              <p className="muted">
                <span className="mono">{pages.length}</span>{' '}
                {pages.length === 1 ? 'page' : 'pages'} selected
              </p>
            )}

            <div className="row">
              <button
                className="btn btn--primary"
                disabled={importing || pages.length === 0 || photoForm.title.trim().length < 2}
                onClick={() => void importPhotos()}
              >
                {importing ? 'Reading the pages…' : 'Read these pages'}
              </button>
            </div>
          </section>
        )}
        {rejected && rejected.length > 0 && (
          <div className="panel">
            <h2 className="panel__title">What the importer found</h2>
            <DiagnosticList diagnostics={rejected} />
          </div>
        )}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : tests.length === 0 ? (
          <div className="empty">
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--step-1)' }}>
              No papers yet
            </p>
            <p className="muted" style={{ maxWidth: '52ch', margin: '0 auto' }}>
              Start from the template, write your Listening, Reading or Writing paper into it, then
              import the Word or PDF file. The module is read from the file, so it lands in the
              right section on its own.
            </p>
          </div>
        ) : (
          <>
          {/*
            * The two shelves. A centre teaches from a handful of papers and
            * keeps years of them; this is which of the two the library is
            * showing, not what may be done with either.
            */}
          <div className="listbar" style={{ marginBottom: 'var(--s-2)' }}>
            <div className="groupbar__chips">
              <span className="eyebrow">Shelf</span>
              <button
                type="button"
                className={`chip ${shelf === 'CURRENT' ? 'chip--on' : ''}`}
                onClick={() => setShelf('CURRENT')}
              >
                Current <span className="mono">{current}</span>
              </button>
              <button
                type="button"
                className={`chip ${shelf === 'PAST' ? 'chip--on' : ''}`}
                onClick={() => setShelf('PAST')}
              >
                Past papers <span className="mono">{kept}</span>
              </button>
            </div>
            <p className="field__hint" style={{ maxWidth: '46ch' }}>
              A past paper can still be set on a class. Archiving is the other thing: an archived
              paper cannot be set at all.
            </p>
          </div>

          {/*
            * The two halves of the library, chosen the same way the shelf is.
            * One at a time: a centre reads one kind or the other, and stacking
            * both made the page scroll past everything a teacher was not
            * looking for to reach the thing they were.
            */}
          <div className="listbar" style={{ marginBottom: 'var(--s-2)' }}>
            <div className="groupbar__chips">
              <span className="eyebrow">Group</span>
              {COLLECTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`chip ${half === option ? 'chip--on' : ''}`}
                  onClick={() => setHalfChosen(option)}
                >
                  {COLLECTION_LABEL[option]} <span className="mono">{split[option].length}</span>
                </button>
              ))}
            </div>
          </div>

          {shelved.length === 0 ? (
            <div className="empty">
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--step-1)' }}>
                {shelf === 'PAST' ? 'Nothing on this shelf yet' : 'Every paper is a past paper'}
              </p>
              <p className="muted" style={{ maxWidth: '52ch', margin: '0 auto' }}>
                {shelf === 'PAST'
                  ? 'Papers you have finished teaching from go here. Move one across from the current shelf and it stays assignable — it is simply out of the way.'
                  : 'Your whole library is on the past-papers shelf. Bring one back, or import a new paper, to have something to teach from this term.'}
              </p>
            </div>
          ) : (
            <>
            {/* Always, however short the library is. Hiding it under a size
                threshold made a feature that exists look like one that does not:
                a centre with three papers saw no box and reasonably concluded
                there was no search. One input is cheaper than that doubt. */}
            {(
              <div className="listbar">
                <input
                  type="search"
                  className="field__input listbar__search"
                  placeholder="Find a paper by title, test or status"
                  aria-label="Find a paper by title, whole test, or status"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query.trim() && (
                  <p className="muted">
                    <span className="mono">{found.length}</span> of{' '}
                    <span className="mono">{shelved.length}</span>{' '}
                    {shelved.length === 1 ? 'paper' : 'papers'}
                    {shelf === 'PAST' ? ' on this shelf' : ''}
                  </p>
                )}
              </div>
            )}

            {/*
              * The shelf itself: one card per whole test, its three papers
              * inside it in the order they are sat, counting down from the
              * newest test. A paper is on this screen once and once only —
              * inside its test if it has one, under "papers not in a test" if
              * it has not — because the layout this replaced showed each one
              * twice and a centre deleting the duplicates lost the originals.
              */}
            {/*
              * The half on screen, counting down from its newest. Its name is
              * on the chip that chose it, so it is not repeated as a heading
              * over the only thing showing.
              */}
            {split[half].length === 0 && (
              <p className="muted">
                {half === 'ACTUAL'
                  ? 'No actual tests yet. Open a test on the other shelf and press “Move to Actual Tests”.'
                  : 'Every test is filed under Actual Tests.'}
              </p>
            )}
            {(() => {
              const inHalf = split[half].filter((test) => test.papers.some(shows));
              /*
               * The shelf as books, each behind a heading that opens it.
               *
               * A dozen Cambridge books of four tests each is fifty tall
               * cards, and a teacher works inside one book at a time. So the
               * books arrive shut, with the newest open: what you see first
               * is an index of the shelf, and one click is the book you are
               * teaching from.
               */
              const books = booksOf(inHalf);
              const shelved = books.filter((book) => book.label !== null).length;
              const isOpen = (book: Book, index: number) => {
                // A search that hid its own results behind a shut heading
                // would read as "nothing found".
                if (searching) return true;
                // What belongs to no book has no heading to hide behind.
                if (book.label === null) return true;
                const chosen = openBooks[book.label];
                if (chosen !== undefined) return chosen;
                // One book is not a shelf worth folding up.
                return shelved <= 1 || index === 0;
              };
              const toggle = (label: string, openNow: boolean) =>
                setOpenBooks((was) => {
                  const next = { ...was, [label]: !openNow };
                  rememberBooks(next);
                  return next;
                });

              return (
                <>
                  {books.map((book, bookIndex) => {
                    const open = isOpen(book, bookIndex);
                    return (
                      <div key={book.label ?? ' no-book'} className="book">
                        {book.label !== null && (
                          <h2 className="book__head">
                            <button
                              type="button"
                              className="book__toggle"
                              aria-expanded={open}
                              aria-controls={bookId(book.label)}
                              onClick={() => toggle(book.label!, open)}
                            >
                              <span className="book__caret" aria-hidden="true" />
                              <span className="book__name">{book.label}</span>
                              <span className="book__count">
                                {book.tests.length} {book.tests.length === 1 ? 'test' : 'tests'}
                              </span>
                            </button>
                          </h2>
                        )}
                        {open && (
                          <div
                            className="book__tests"
                            id={book.label === null ? undefined : bookId(book.label)}
                          >
                            {book.tests.map((test) => {
              const missing = whatIsMissing(test);
              const settable = test.papers.some((paper) => paper.status === 'PUBLISHED');
              // Of three, because a test is three papers. A spare is counted
              // separately rather than added in: "4 of 3 papers" is not a
              // sentence anybody should have to read.
              const held = test.slots.filter((slot) => slot.paper).length;
              return (
                <section key={test.name} className="testcard">
                  <header className="testcard__head">
                    <div>
                      {renaming === test.name ? (
                        /* The name in a box, with the two things that can be
                           done to it beside it. No hidden double-click: a
                           button said "Rename" and this is what it opened. */
                        <div className="row" style={{ gap: 'var(--s-2)' }}>
                          <input
                            className="field__input"
                            /* A test is called "Actual Test 1", not a
                               paragraph. Sized to the name so the two buttons
                               stay on the line with it. */
                            style={{ flex: '0 1 20rem' }}
                            autoFocus
                            value={draftName}
                            maxLength={120}
                            aria-label={`New name for ${test.name}`}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void renameTest(test, draftName);
                              if (e.key === 'Escape') setRenaming(null);
                            }}
                          />
                          <button
                            className="btn btn--primary"
                            disabled={working !== null || !draftName.trim()}
                            onClick={() => void renameTest(test, draftName)}
                          >
                            Save
                          </button>
                          <button className="btn" onClick={() => setRenaming(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                      <h2 className="testcard__title">
                        {test.name}
                        {/* Whose paper this is, said once and at the top. A
                            borrowed test looks exactly like an owned one
                            otherwise, and the difference decides what half
                            these buttons will do. */}
                        {!test.owned && (
                          <span className="tag tag--lent">
                            {test.platform ? 'From the platform' : 'Shared'}
                          </span>
                        )}
                        {test.owned && test.shared && (
                          <span className="tag tag--lent">Shared with every centre</span>
                        )}
                      </h2>
                      )}
                      <p className="muted">
                        {!test.owned && test.platform
                          ? 'Every centre teaches from this · '
                          : !test.owned && test.sharedFrom
                            ? `From ${test.sharedFrom} · `
                            : ''}
                        <span className="mono">{held}</span> of 3 papers
                        {missing.length > 0 ? ` · no ${missing.join(' or ')} yet` : ' · complete'}
                        {test.extras.length > 0
                          ? ` · ${test.extras.length} spare`
                          : ''}
                      </p>
                    </div>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      {/* A centre sets Test 1, not three papers in a row over
                          the same register. */}
                      <button
                        className="btn btn--primary"
                        disabled={!settable}
                        title={settable ? undefined : 'Publish a paper of this test first'}
                        onClick={() => setSetting(test)}
                      >
                        Set on a class
                      </button>
                      {/* Everything below changes the papers themselves, so
                          it belongs to the centre that made them. A borrowed
                          test can be set on a class and nothing else. */}
                      {test.owned && (
                        <>
                          {/* The name is the grouping, so this reaches all
                              three papers at once — see renameTest. */}
                          <button
                            className="btn"
                            disabled={working !== null}
                            onClick={() => {
                              setDraftName(test.name);
                              setRenaming(test.name);
                            }}
                          >
                            Rename
                          </button>
                          <button
                            className="btn"
                            disabled={working !== null}
                            onClick={() => void shareTest(test, !test.shared)}
                            title={
                              test.shared
                                ? 'Every centre on the platform can teach from this test'
                                : 'Let every centre on the platform teach from this test'
                            }
                          >
                            {test.shared ? 'Stop sharing' : 'Share with every centre'}
                          </button>
                          {/* Which half of the library it is filed under.
                              Worded as the move it makes, like the shelf
                              button beside it, so a teacher reads what will
                              happen rather than what is already true. */}
                          <button
                            className="btn"
                            disabled={working !== null}
                            onClick={() =>
                              void fileTest(test, test.collection === 'ACTUAL' ? 'GENERAL' : 'ACTUAL')
                            }
                            title={
                              test.collection === 'ACTUAL'
                                ? 'File this among the ordinary tests'
                                : 'File this among the tests recalled from real sittings'
                            }
                          >
                            {test.collection === 'ACTUAL' ? 'Move to Tests' : 'Move to Actual Tests'}
                          </button>
                          <button
                            className="btn"
                            disabled={working !== null}
                            onClick={() =>
                              void moveTest(test, shelf === 'CURRENT' ? 'PAST' : 'CURRENT')
                            }
                          >
                            {shelf === 'CURRENT' ? 'Move to past papers' : 'Bring back'}
                          </button>
                          {/* The whole test at once, which is what a centre
                              means when it clears one out. Doing it a paper at
                              a time is how three papers get deleted by somebody
                              who meant to delete none of them. */}
                          <button
                            className="btn btn--danger"
                            disabled={working !== null}
                            onClick={() => void removeTest(test)}
                          >
                            Delete test
                          </button>
                        </>
                      )}
                    </div>
                  </header>

                  <div className="ledger__wrap">
                    {/* 13rem: two buttons side by side. At 11rem they wrapped,
                        which doubled the height of every row. */}
                    <table
                      className="ledger ledger--test"
                      style={
                        { '--actions': '12.5rem', '--named': '32.25rem' } as CSSProperties
                      }
                    >
                      <LibraryColumns compact />
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th>Paper</th>
                          <th>Status</th>
                          <th>Questions</th>
                          <th>Length</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {test.slots.map(({ module, paper }) =>
                          paper && !shows(paper) ? null : paper ? (
                            <PaperRow
                              key={module}
                              module={MODULE_LABEL[module]}
                              paper={paper}
                              showImported={false}
                              shelf={shelf}
                              working={working}
                              onArchive={test.owned ? archive : undefined}
                              onDelete={test.owned ? remove : undefined}
                            />
                          ) : searching || !test.owned ? null : (
                            /*
                             * An empty slot is the way to fill it. "Test 1 ·
                             * Reading · not added" is a sentence about work to
                             * do, and the only way to do it used to be to go
                             * to the paste screen and say both of those things
                             * again from memory.
                             */
                            <tr key={module} className="testcard__gap">
                              <td>
                                <ModuleTag module={module} />
                              </td>
                              <td colSpan={4} className="muted">
                                Not added yet
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <Link
                                  className="btn"
                                  to={`/tests/new?module=${module}&set=${encodeURIComponent(test.name)}`}
                                >
                                  Add the {MODULE_LABEL[module]}
                                </Link>
                              </td>
                            </tr>
                          ),
                        )}
                        {/* A module the test has two of. Nothing hides it: a
                            paper nobody can see is a paper nobody can fix. */}
                        {test.extras.filter(shows).map((paper) => (
                          <PaperRow
                            key={paper.id}
                            module={`${MODULE_LABEL[paper.module as Module]} (spare)`}
                            paper={paper}
                            showImported={false}
                            shelf={shelf}
                            working={working}
                            onArchive={test.owned ? archive : undefined}
                            onDelete={test.owned ? remove : undefined}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
                );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              );
            })()}

            {/*
              * What is left: papers named after no test. They cannot be set on
              * a class until they are part of one, so this is a list of work to
              * do rather than a second library.
              */}
            {library.loose.filter(shows).length > 0 && (
              <section className="stack" style={{ gap: 'var(--s-3)' }}>
                <div className="library__head">
                  <h2 className="library__title">Papers not in a test</h2>
                  <p className="muted">
                    <span className="mono">{library.loose.filter(shows).length}</span>{' '}
                    {library.loose.filter(shows).length === 1 ? 'paper' : 'papers'}
                  </p>
                </div>
                <p className="field__hint" style={{ maxWidth: '62ch' }}>
                  A paper on its own cannot be set on a class. Open it and name the test it belongs
                  to — three papers sharing a name make one test, and it moves up into it. A paper
                  marked Shared or Platform is named by the centre that owns it, not here.
                </p>

                <div className="ledger__wrap">
                  {/* Three buttons here, not two: a paper in no test can be
                      shelved on its own, because there is no test to split. */}
                  <table
                    className="ledger ledger--test"
                    style={{ '--actions': '19rem' } as CSSProperties}
                  >
                    <LibraryColumns />
                    <thead>
                      <tr>
                        <th>Module</th>
                        <SortHeader label="Paper" column="title" sort={sort} onSort={sortBy} />
                        <SortHeader label="Status" column="status" sort={sort} onSort={sortBy} />
                        <SortHeader
                          label="Questions"
                          column="questions"
                          sort={sort}
                          onSort={sortBy}
                          numeric
                        />
                        <th>Length</th>
                        <SortHeader
                          label="Imported"
                          column="imported"
                          sort={sort}
                          onSort={sortBy}
                        />
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {arrangePapers(library.loose.filter(shows), {
                        query: '',
                        key: sort.key,
                        direction: sort.direction,
                      }).map((paper) => (
                        <PaperRow
                          key={paper.id}
                          module={MODULE_LABEL[paper.module as Module] ?? paper.module}
                          paper={paper}
                          mark={
                            paper.owned === false ? (paper.platform ? 'Platform' : 'Shared') : null
                          }
                          shelf={shelf}
                          working={working}
                          /* A paper here can still be somebody else's: the
                             platform's own papers land in a centre's library
                             and are read-only to it. Offering Archive and
                             Delete on one is offering four hundred and four. */
                          onMove={paper.owned === false ? undefined : moveTo}
                          onArchive={paper.owned === false ? undefined : archive}
                          onDelete={paper.owned === false ? undefined : remove}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {!library.tests.some((test) => test.papers.some(shows)) &&
              library.loose.filter(shows).length === 0 && (
              <p className="muted library__blank">Nothing matches that search.</p>
            )}
            </>
          )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * One paper, wherever it is shown.
 *
 * The same row serves a paper inside its test and a paper belonging to none,
 * so the two can never drift into showing different things about the same
 * paper — or, worse, offering different buttons for it.
 */
function PaperRow({
  module,
  paper,
  mark = null,
  showImported = true,
  shelf,
  working,
  onMove,
  onArchive,
  onDelete,
}: {
  /** Listening, Reading or Writing — the column that makes a test read as one. */
  module: string;
  paper: TestSummary;
  /**
   * Whether the row carries the date the paper was imported.
   *
   * Left out inside a test card, where the columns have to reach the buttons
   * on a 1280-wide laptop and this is the one a teacher looking at a test is
   * least likely to want. It stays on the loose papers, which are sorted by
   * it.
   */
  showImported?: boolean;
  shelf: Shelf;
  working: string | null;
  /**
   * Whose paper it is, where that is not already said above the row.
   *
   * Inside a test card the heading carries it once, so every row repeating it
   * was three more copies of the same word at three different distances from
   * the left — which is what a ragged column looks like. Under "papers not in
   * a test" there is no heading to carry it, so the row does.
   */
  mark?: string | null;
  /**
   * Moving this one paper to the other shelf — offered for a paper that
   * belongs to no test, and withheld inside a test. A test moves as a test:
   * shelving one of its three papers on its own leaves a test with a Writing
   * paper missing on one shelf and a Writing paper alone on the other.
   */
  onMove?: (paper: TestSummary, next: Shelf) => void;
  /**
   * Archiving and deleting belong to the centre that owns the paper. Left out
   * for one borrowed from the platform, where the row is a thing to teach
   * from and not a thing to manage.
   */
  onArchive?: (paper: TestSummary) => void;
  onDelete?: (paper: TestSummary) => void;
}) {
  const busy = working !== null;
  const sat = paper.sittings ?? 0;
  return (
    <tr>
      <td>
        <ModuleTag module={paper.module as TestModule} />
        {/spare/.test(module) && <span className="muted"> · spare</span>}
      </td>
      <td style={{ fontWeight: 600 }}>
        <Link to={`/tests/${paper.id}`}>{paper.title}</Link>
        {/* Listening has no Academic or General Training to be, so it says
            nothing rather than saying "Academic" untruthfully. */}
        {paper.module !== 'LISTENING' && (
          <span className="muted"> · {paper.variant === 'GENERAL' ? 'General Training' : 'Academic'}</span>
        )}
        {mark && <span className="tag tag--lent">{mark}</span>}
      </td>
      <td>
        <span className={`tag ${STATUS_TONE[paper.status] ?? ''}`}>{paper.status}</span>
      </td>
      <td className="num">
        {paper.module === 'WRITING' ? `${paper.questionCount}/2` : `${paper.questionCount}/40`}
      </td>
      <td className="mono muted">
        {paper.module === 'LISTENING'
          ? paper.hasAudio
            ? 'audio + 1m50s'
            : 'no audio'
          : formatDuration(60 * 60_000)}
      </td>
      {showImported && <td className="mono muted">{paper.createdAt.slice(0, 10)}</td>}
      <td style={{ textAlign: 'right' }}>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {onMove && (
            <button
              className="btn"
              disabled={busy}
              onClick={() => onMove(paper, shelf === 'CURRENT' ? 'PAST' : 'CURRENT')}
            >
              {shelf === 'CURRENT' ? 'To past' : 'Bring back'}
            </button>
          )}
          {onArchive && paper.status !== 'ARCHIVED' && (
            <button className="btn" disabled={busy} onClick={() => onArchive(paper)}>
              Archive
            </button>
          )}
          {/*
            * Deleting is refused once anybody has sat the paper, because their
            * results hang off it. The row says so rather than the button
            * pressing and a message appearing at the top of a page the teacher
            * scrolled past long ago — which is indistinguishable from a button
            * that does nothing.
            */}
          {onDelete &&
            paper.status !== 'PUBLISHED' &&
            (sat > 0 ? (
              /*
               * Not just why it cannot be deleted, but where to go about it.
               * The sittings are what stand in the way, and clearing them is
               * done on the results screen — which had no way in from here,
               * so the row was a dead end that stated a fact.
               */
              <Link
                className="muted"
                to={`/results?testId=${paper.id}`}
                title={`${sat} ${sat === 1 ? 'candidate has' : 'candidates have'} sat this paper. Their ${sat === 1 ? 'result' : 'results'} would go with it — clear ${sat === 1 ? 'it' : 'them'} first to delete the paper.`}
              >
                {sat} sat it
              </Link>
            ) : (
              <button className="btn btn--danger" disabled={busy} onClick={() => onDelete(paper)}>
                {working === paper.id ? 'Working…' : 'Delete'}
              </button>
            ))}
        </div>
      </td>
    </tr>
  );
}

